import React, { useState } from "react";
import { FaTimes } from "react-icons/fa";

const EMPTY = { employeeId: "", basicSalary: 0, allowances: 0, bonus: 0, deductions: 0, taxDeduction: 0, notes: "" };

function HrPayrollItemModal({ show, onClose, onSubmit, initial = null, employees = [], isSubmitting, error }) {
  const [form, setForm] = useState(() =>
    initial ? { ...EMPTY, ...initial, employeeId: initial.employeeId || "" } : { ...EMPTY }
  );

  if (!show) return null;

  const set = (f, v) => setForm((p) => ({ ...p, [f]: v }));

  const gross = Number(form.basicSalary) + Number(form.allowances) + Number(form.bonus);
  const net   = gross - Number(form.deductions) - Number(form.taxDeduction);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ ...form, grossSalary: gross, netSalary: net });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4 dark:bg-black/60">
      <button type="button" onClick={onClose} className="absolute inset-0" />
      <div className="relative w-full max-w-lg rounded-2xl border border-app-primary bg-app-card shadow-2xl dark:shadow-black/50">
        <div className="flex items-center justify-between border-b border-app-primary px-6 py-4">
          <h3 className="text-lg font-semibold text-app-heading">{initial ? "Edit Payroll Item" : "Add Employee to Payroll"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-app-muted transition-colors hover:bg-gray-100 hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:hover:bg-slate-700"><FaTimes /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {error && <p className="rounded-lg border border-app-error bg-app-error px-3 py-2 text-sm text-app-error">{error}</p>}
          {!initial && (
            <div>
              <label htmlFor="pi-employee" className="mb-1 block text-sm font-medium text-app-secondary">Employee <span className="text-red-500 dark:text-red-400">*</span></label>
              <select id="pi-employee" required value={form.employeeId} onChange={(e) => set("employeeId", e.target.value)}
                className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-500/20 [color-scheme:light] dark:[color-scheme:dark]">
                <option value="">— Select Employee —</option>
                {employees.map((emp) => (
                  <option key={emp.id} value={emp.id}>{emp.fullName} {emp.employeeCode ? `(${emp.employeeCode})` : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {[
              ["pi-basic",    "basicSalary",  "Basic Salary"],
              ["pi-allow",    "allowances",   "Allowances"],
              ["pi-bonus",    "bonus",        "Bonus"],
              ["pi-ded",      "deductions",   "Deductions"],
              ["pi-tax",      "taxDeduction", "Tax Deduction"],
            ].map(([id, field, label]) => (
              <div key={field}>
                <label htmlFor={id} className="mb-1 block text-sm font-medium text-app-secondary">{label}</label>
                <input id={id} type="number" min="0" step="0.01" value={form[field]}
                  onChange={(e) => set(field, e.target.value)}
                  className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-500/20 [color-scheme:light] dark:[color-scheme:dark]" />
              </div>
            ))}
          </div>
          <div className="rounded-lg bg-app-secondary border border-app-primary px-4 dark:bg-slate-900/60 py-3 grid grid-cols-2 gap-2 text-sm">
            <span className="text-app-muted">Gross Salary</span><span className="font-semibold text-app-primary">{gross.toFixed(2)}</span>
            <span className="text-app-muted">Net Salary</span><span className="font-bold text-[#2470B7] dark:text-blue-400">{net.toFixed(2)}</span>
          </div>
          <div>
            <label htmlFor="pi-notes" className="mb-1 block text-sm font-medium text-app-secondary">Notes</label>
            <textarea id="pi-notes" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
              className="w-full rounded-lg border border-app-primary bg-app-input px-3 py-2 text-sm text-app-primary placeholder:text-app-muted outline-none focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 dark:focus:border-blue-500 dark:focus:ring-blue-500/20 [color-scheme:light] dark:[color-scheme:dark] resize-none" />
          </div>
          <div className="flex justify-end gap-3 border-t border-app-primary pt-4">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-app-primary bg-app-card px-5 py-2 text-sm font-semibold text-app-secondary transition-colors hover:bg-gray-50 hover:text-app-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 dark:hover:bg-slate-700">Cancel</button>
            <button type="submit" disabled={isSubmitting}
              className="rounded-lg bg-[#2470B7] border border-[#2470B7] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#1f5f9b] focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#2470B7] dark:border-blue-500 dark:bg-blue-600 dark:hover:bg-blue-500 dark:disabled:hover:bg-blue-600">
              {isSubmitting ? "Saving…" : initial ? "Save Changes" : "Add to Payroll"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default HrPayrollItemModal;
